/**
 * Story reactions — emoji acknowledgements bound to (storyId, reactingUserId).
 *
 * A reaction is an encrypted control message in the pairwise E2EE envelope
 * between the reactor and the story author. It rides the same channel as
 * receipts and tombstones, so the server cannot tell that a payload was a
 * reaction by length or prefix.
 *
 * Hardening:
 *
 * 1. **Reactor authentication via E2EE envelope.** The outer envelope is
 *    signed by the reactor's identity key.
 *
 * 2. **Single-scalar emoji enforcement.** Reactions must be either empty
 *    (revoke) or a *single* Unicode scalar. ZWJ sequences like the family
 *    emoji are rejected — they are visually one grapheme but several scalars,
 *    and we keep the validation surface small and deterministic. Story reactions
 *    are picked from a curated set of base emoji.
 *
 * 3. **Revoke is a first-class state.** Empty `emoji` means "revoke my prior
 *    reaction." The store applies LWW by `ts`.
 *
 * Wire format of the *plaintext that gets encrypted*:
 *
 *   kaether:story:react:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "storyId": "01H...", "reactingUserId": "bob",
 *     "emoji": "😀" /* or "" to revoke *\/, "ts": 1700000000 }
 *
 * Mirrors iOS `kaether_for_ios/Stoat/E2EE/StoryReaction.swift`.
 */

import type { StoryError } from "./Story";

// ---------------------------------------------------------------------------
// Payload type
// ---------------------------------------------------------------------------

export interface StoryReactionPayload {
  /** Always 1. */
  readonly schema: number;
  readonly storyId: string;
  readonly reactingUserId: string;
  /** Single Unicode scalar OR empty string (revoke). */
  readonly emoji: string;
  /** Unix seconds. */
  readonly ts: number;
}

/** Current reaction payload schema version. */
export const STORY_REACTION_CURRENT_SCHEMA = 1;

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/** Wire-prefix for story-reaction control messages. */
export const STORY_REACTION_PREFIX = "kaether:story:react:v1:";

/** Returns true if the plaintext looks like a story reaction. */
export function isStoryReaction(s: string): boolean {
  return s.startsWith(STORY_REACTION_PREFIX);
}

/**
 * Encodes a `StoryReactionPayload` to the wire string.
 *
 * Validates `emoji` on encode (empty = revoke; otherwise exactly one Unicode
 * scalar). Throws `StoryError.malformed` for multi-scalar sequences such as
 * ZWJ family emoji.
 *
 * Mirrors iOS `StoryReactionCodec.encode(_:)`.
 */
export function encodeStoryReaction(payload: StoryReactionPayload): string {
  validateEmoji(payload.emoji);
  if (!payload.storyId || payload.storyId.length === 0) {
    throw { kind: "malformed", reason: "empty storyId" } satisfies StoryError;
  }
  if (!payload.reactingUserId || payload.reactingUserId.length === 0) {
    throw {
      kind: "malformed",
      reason: "empty reactingUserId",
    } satisfies StoryError;
  }
  const json = JSON.stringify(payload);
  return STORY_REACTION_PREFIX + base64UrlEncode(new TextEncoder().encode(json));
}

/**
 * Decodes and validates a story-reaction wire string.
 *
 * Repeats emoji validation on decode as defence-in-depth against a
 * non-conforming peer.
 *
 * Mirrors iOS `StoryReactionCodec.decode(_:)`.
 */
export function decodeStoryReaction(s: string): StoryReactionPayload {
  if (!s.startsWith(STORY_REACTION_PREFIX)) {
    throw {
      kind: "malformed",
      reason: "not a story reaction",
    } satisfies StoryError;
  }
  const b64 = s.slice(STORY_REACTION_PREFIX.length);
  let jsonBytes: Uint8Array;
  try {
    jsonBytes = base64UrlDecode(b64);
  } catch {
    throw {
      kind: "malformed",
      reason: "invalid base64url",
    } satisfies StoryError;
  }
  let payload: StoryReactionPayload;
  try {
    payload = JSON.parse(new TextDecoder().decode(jsonBytes));
  } catch {
    throw {
      kind: "malformed",
      reason: "json decode failed",
    } satisfies StoryError;
  }
  if (payload.schema !== STORY_REACTION_CURRENT_SCHEMA) {
    throw {
      kind: "unsupportedSchema",
      schema: payload.schema,
    } satisfies StoryError;
  }
  if (!payload.storyId || payload.storyId.length === 0) {
    throw { kind: "malformed", reason: "empty storyId" } satisfies StoryError;
  }
  if (!payload.reactingUserId || payload.reactingUserId.length === 0) {
    throw {
      kind: "malformed",
      reason: "empty reactingUserId",
    } satisfies StoryError;
  }
  validateEmoji(payload.emoji);
  return payload;
}

/**
 * Convenience: returns null for non-reaction plaintexts. Throws only when
 * the payload claimed to be a reaction but was malformed.
 *
 * Mirrors iOS `StoryReactionCodec.tryDecode(_:)`.
 */
export function tryDecodeStoryReaction(
  s: string,
): StoryReactionPayload | null {
  if (!isStoryReaction(s)) return null;
  return decodeStoryReaction(s);
}

// ---------------------------------------------------------------------------
// Emoji validation
// ---------------------------------------------------------------------------

/**
 * Empty string means "revoke." Otherwise the string must consist of exactly
 * one Unicode scalar. Multi-scalar sequences (e.g. ZWJ family emoji composed
 * from U+1F468 U+200D U+1F469 U+200D U+1F467) are rejected.
 *
 * Mirrors iOS `StoryReactionCodec.validateEmoji(_:)`.
 */
function validateEmoji(emoji: string): void {
  if (emoji.length === 0) return;
  // Spread into Unicode code points (each element = one scalar).
  const scalars = [...emoji];
  if (scalars.length !== 1) {
    throw {
      kind: "malformed",
      reason: `emoji must be empty or single unicode scalar (got ${scalars.length} scalars)`,
    } satisfies StoryError;
  }
}

// ---------------------------------------------------------------------------
// Base64url helpers
// ---------------------------------------------------------------------------

function base64UrlEncode(data: Uint8Array): string {
  const binStr = Array.from(data)
    .map((b) => String.fromCharCode(b))
    .join("");
  return btoa(binStr)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(s: string): Uint8Array {
  let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (b64.length % 4)) % 4;
  b64 += "=".repeat(pad);
  const binStr = atob(b64);
  const bytes = new Uint8Array(binStr.length);
  for (let i = 0; i < binStr.length; i++) {
    bytes[i] = binStr.charCodeAt(i);
  }
  return bytes;
}
